/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import ResizeObserver from 'react-resize-observer';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import moment from 'moment';
import HeaderDropdownMenu from './HeaderDropdownMenu';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import {
  setHeaderHeight, setSelectedDate, setSelectedProgramDayNumber, setActiveModal, setSelectedWeekViewDayToggle,
} from '../../ducks/calendarActions';
import BreadCrumbs from './BreadCrumbs';
import GearIconButtons from './GearIconButtons';
import CloudUploadIcon from './CloudUploadIcon';

const MenuWrapper = styled('div')`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const HeaderTextWrapper = styled('div')`
 cursor: pointer;
    transition: ${TransitionGlobal};
    :hover {
      opacity: 0.7;
    }
`;

const HeaderContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 8px;
  flex-wrap: wrap;
  gap: 20px;
  border-bottom: 1px solid #EEEEEE;
  background-color: ${(props) => props.bgColor};
  ${(props) => props.viewType === 'weekView' && 'position: fixed;'}
  z-index: 99;
`;

const NavArrowButton = styled('div')`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border-radius: 50%;
  padding: 0;
  width: 32px;
  height: 32px;
  transition: all 250ms ease-in-out;
  position: relative;
  
  &:hover {
    background-color: rgba(238, 238, 238, 0.6);
  }
  
  &:active {
    background-color: rgba(238, 238, 238, 0.9);
    transition: background-color 100ms ease-in-out;
  }
  
  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const HeaderAndDropdownWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 32px;
`;

const BreadCrumbsAndNameWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  
`;

const TodayButton = styled('div')`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #EEEEEE;
  border-radius: 24px;
  min-height: 35px;
  height: 35px;
  min-width: 85px;
  box-shadow: 0px 1px 3px 0px #3031331A;
  padding: 4px 16px;
  transition: all 250ms ease-in-out;
  user-select: none;
  
  &:hover {
    background-color: rgba(238, 238, 238, 0.6);
  }
  
  &:active {
    background-color: rgba(238, 238, 238, 0.9);
    transition: background-color 100ms ease-in-out;
  }
`;

const DaySelectBox = styled('div')`
  width: 174px; /* Reduced from 232px by 25% */
`;

const HeaderComponent = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const headerRef = useRef(null);
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const headerHeight = useSelector((state) => state.calendar.data.headerHeight);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const nonDateBasedProgramItems = useSelector((state) => state.calendar.data.nonDateBasedProgramItems);
  const groupByFilter = useSelector(
    (state) => state.calendar.data.groupByFilter,
  );

  const [showDropdown, setShowDropdown] = useState(false);
  const hasRun = useRef(false);

  /** this funtion will run once on mount in weekview
   * this will set the start of the week to sunday by default
   * on inital load
   * after that the user selected date will always be the start of the week
   */
  useEffect(() => {
    if (
      !hasRun.current
    && viewType === 'weekView'
    && selectedDate
    && !moment(selectedDate).isSame(moment(selectedDate).startOf('week'), 'day')
    ) {
      dispatch(setSelectedDate(moment(selectedDate).startOf('week')));
      hasRun.current = true;
    }
  }, [viewType, selectedDate, dispatch]);

  useEffect(() => {
    if (headerRef.current) {
      const height = headerRef.current.offsetHeight;
      if (height !== headerHeight) {
        dispatch(setHeaderHeight(height));
      }
    }
  }, []); /** Run only on mount, then we will measure on resize in the resize observer */

  const getFirstToggleDayOfNextMonth = (targetWeekdayName) => {
    const startOfNextMonth = moment(selectedDate)
      .add(1, 'month')
      .startOf('month');

    while (startOfNextMonth.format('dddd') !== targetWeekdayName) {
      startOfNextMonth.add(1, 'day');
    }

    return startOfNextMonth.toDate();
  };

  /** Function to add a day/week to the selected date */
  const addDay = () => {
    if (viewType === 'weekView') {
      if (programType === 'free-form-program') {
        if (groupByFilter === 'Day') {
          const itemsArray = Object.values(nonDateBasedProgramItems);

          const currentItem = nonDateBasedProgramItems[selectedProgramDayNumber];
          const currentDayInWeek = currentItem?.dayInWeek;

          /** If it's already day 7, stop — no next day exists */
          if (currentDayInWeek === 7) {
            return;
          }

          const nextDayInWeek = currentDayInWeek + 1;

          /** Find all items matching the next day */
          const matchingItems = itemsArray.filter((item) => item.dayInWeek === nextDayInWeek);

          if (matchingItems.length === 0) {
            return;
          }

          /** Pick the one with the lowest weekNumber */
          const nextItem = matchingItems.reduce((min, item) => (item.weekNumber < min.weekNumber ? item : min));

          dispatch(setSelectedProgramDayNumber(nextItem.dayNumber));
        }

        if (groupByFilter === 'Week') {
          const currentWeekNumber = nonDateBasedProgramItems[selectedProgramDayNumber]?.weekNumber;
          const targetWeek = currentWeekNumber + 1;

          /** Get dayNumber of the first day in the next week */
          const dayNumberOfNextWeekStart = Object.values(nonDateBasedProgramItems).find(
            (item) => item.weekNumber === targetWeek,
          )?.dayNumber;

          if (currentWeekNumber && dayNumberOfNextWeekStart) {
            dispatch(setSelectedProgramDayNumber(dayNumberOfNextWeekStart));
          }
        }
      }
      if (programType !== 'free-form-program') {
      /** For week view, move to start of next week */
        const nextWeekStart = moment(selectedDate)
          .add(7, 'days')
          .toDate();
        dispatch(
          setSelectedDate(
            selectedWeekViewDayToggle !== 'Everyday'
              ? getFirstToggleDayOfNextMonth(selectedWeekViewDayToggle)
              : nextWeekStart,
          ),
        );
      }
    } else {
    /** For program library day view, add 1 index and get dayNumber at index until hit last day of program */
      const sortedDayNumbers = Object.keys(nonDateBasedProgramItems)
        .map(Number)
        .sort((a, b) => a - b);
      const currentIndex = sortedDayNumbers.indexOf(selectedProgramDayNumber);
      const nextSelectedProgramDayNumber = sortedDayNumbers[currentIndex + 1];

      if (
        programType === 'free-form-program'
      && nextSelectedProgramDayNumber !== undefined
      ) {
        dispatch(setSelectedProgramDayNumber(nextSelectedProgramDayNumber));
      }

      /** For calendar day view, add 1 day */
      if (programType !== 'free-form-program') {
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);
        dispatch(setSelectedDate(nextDay));
      }
    }
  };

  const getFirstToggleDayOfPreviousMonth = (targetWeekdayName) => {
    const startOfPrevMonth = moment(selectedDate)
      .subtract(1, 'month')
      .startOf('month');

    while (startOfPrevMonth.format('dddd') !== targetWeekdayName) {
      startOfPrevMonth.add(1, 'day');
    }

    return startOfPrevMonth.toDate();
  };

  /** Function to subtract a day/week from the selected date */
  const subtractDay = () => {
    if (viewType === 'weekView') {
      if (programType === 'free-form-program') {
        if (groupByFilter === 'Day') {
          const itemsArray = Object.values(nonDateBasedProgramItems);

          const currentItem = nonDateBasedProgramItems[selectedProgramDayNumber];
          const currentDayInWeek = currentItem?.dayInWeek;

          /** If it's already day 1, stop — no previous day exists */
          if (currentDayInWeek === 1) {
            return;
          }

          const prevDayInWeek = currentDayInWeek - 1;

          /** Find all items matching the previous day */
          const matchingItems = itemsArray.filter(
            (item) => item.dayInWeek === prevDayInWeek,
          );

          if (matchingItems.length === 0) {
            return;
          }
          /** Pick the one with the lowest weekNumber */
          const prevItem = matchingItems.reduce((min, item) => (item.weekNumber < min.weekNumber ? item : min));
          dispatch(setSelectedProgramDayNumber(prevItem.dayNumber));
        }
        if (groupByFilter === 'Week') {
          const currentWeekNumber = nonDateBasedProgramItems[selectedProgramDayNumber]?.weekNumber;
          const targetWeek = currentWeekNumber - 1;

          /** Get dayNumber of the first day in the previous week */
          const dayNumberOfPrevWeekStart = Object.values(nonDateBasedProgramItems).find(
            (item) => item.weekNumber === targetWeek,
          )?.dayNumber;

          if (targetWeek >= 1 && dayNumberOfPrevWeekStart !== undefined) {
            dispatch(setSelectedProgramDayNumber(dayNumberOfPrevWeekStart));
          }
        }
      }

      if (programType !== 'free-form-program') {
        const prevWeekStart = moment(selectedDate)
          .subtract(7, 'days')
          .toDate();
        dispatch(
          setSelectedDate(
            selectedWeekViewDayToggle !== 'Everyday'
              ? getFirstToggleDayOfPreviousMonth(selectedWeekViewDayToggle)
              : prevWeekStart,
          ),
        );
      }
    } else {
    /** For program library day view, subtract 1 day using sorted keys */
      if (programType === 'free-form-program') {
        const sortedDayNumbers = Object.keys(nonDateBasedProgramItems)
          .map(Number)
          .sort((a, b) => a - b);

        const currentIndex = sortedDayNumbers.indexOf(selectedProgramDayNumber);
        const prevSelectedProgramDayNumber = sortedDayNumbers[currentIndex - 1];

        if (prevSelectedProgramDayNumber !== undefined) {
          dispatch(setSelectedProgramDayNumber(prevSelectedProgramDayNumber));
        }
      }

      /** For calendar day view, subtract 1 day */
      if (programType !== 'free-form-program') {
        const previousDay = new Date(selectedDate);
        previousDay.setDate(previousDay.getDate() - 1);
        dispatch(setSelectedDate(previousDay));
      }
    }
  };

  /** Function to set the selected date to today */
  const setToday = () => {
    dispatch(setSelectedDate(new Date())); // Set to current date
    if (viewType === 'weekView' && selectedWeekViewDayToggle !== 'Everyday') {
      dispatch(setSelectedWeekViewDayToggle('Everyday'));
    }
  };

  const nameWriter = () => {
    if (Object.keys(selectedCalendar).length) {
      return selectedCalendar?.name ? `${selectedCalendar?.name}` : 'Select Calendar';
    }
    if (Object.keys(selectedReduxAthlete).length) {
      return selectedReduxAthlete?.first && selectedReduxAthlete?.last ? `${selectedReduxAthlete?.first} ${selectedReduxAthlete?.last}` : 'Select Calendar';
    }
    return 'Select Calendar';
  };

  const programNameWriter = () => {
    if (Object.keys(selectedReduxNonDateBasedProgram).length) {
      return selectedReduxNonDateBasedProgram?.programName ? `${selectedReduxNonDateBasedProgram?.programName}` : 'Select Program';
    }
    return 'Select Program';
  };

  return (
    <HeaderContainer ref={headerRef} bgColor={theme.colors.background[0]} viewType={viewType}>
      <ResizeObserver
        onResize={(rect) => {
          if (rect?.height !== headerHeight) {
            dispatch(setHeaderHeight(rect?.height));
          }
        }}
      />
      <HeaderAndDropdownWrapper>
        <HeaderTextWrapper onClick={() => dispatch(setActiveModal('selectCalendarModal'))}>
          <BreadCrumbsAndNameWrapper>
            <BreadCrumbs />
            <Text fontWeight={700} fontSize='22px'>
              <div style={{
                display: 'flex',
                alignItems: 'center',
              }}
              >
                {programType === 'calendar' ? nameWriter() : programNameWriter()}
                <div style={{
                  marginLeft: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                >
                  <IcomoonReact iconSet={iconSet} size={10} icon='down-arrow' color='#444444' />
                </div>
              </div>
            </Text>
          </BreadCrumbsAndNameWrapper>
        </HeaderTextWrapper>
      </HeaderAndDropdownWrapper>
      <MenuWrapper>
        <CloudUploadIcon />
        <NavArrowButton onClick={() => subtractDay()}>
          <IcomoonReact
            iconSet={iconSet}
            size={12}
            icon='left-arrow'
            color='#9E9E9E'
          />
        </NavArrowButton>
        <NavArrowButton onClick={() => addDay()}>
          <IcomoonReact
            iconSet={iconSet}
            size={12}
            icon='right-arrow'
            color='#9E9E9E'
          />
        </NavArrowButton>
        {programType !== 'free-form-program' ? (
          <TodayButton onClick={setToday}>
            <Text fontSize='16px'>Today</Text>
          </TodayButton>
        ) : null}
        <DaySelectBox onClick={() => setShowDropdown(!showDropdown)}>
          <HeaderDropdownMenu />
        </DaySelectBox>
        <GearIconButtons />
      </MenuWrapper>
    </HeaderContainer>
  );
};

export default HeaderComponent;
